const IT_INPUT_DATE_FORMATTER = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const IT_INPUT_TIME_FORMATTER = new Intl.DateTimeFormat('it-IT', {
  hour: '2-digit',
  minute: '2-digit',
});
const IT_INPUT_DATE_TIME_FORMATTER = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export type DateLike = string | number | Date | null | undefined;

function capitalizeItalianMonth(value: string): string {
  if (!value) return value;
  return value.replace(/^[a-zà-ù]/iu, (match) => match.toUpperCase());
}

function parseDateString(value: string): Date | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    const [year, month, day] = trimmed.slice(0, 10).split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(trimmed)) {
    const separator = trimmed.includes('-') ? '-' : '/';
    const [day, month, year] = trimmed.split(separator).map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  return undefined;
}

export function parseDateInput(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  return parseDateString(value);
}

export function coerceDate(value: DateLike): Date | undefined {
  if (!value && value !== 0) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : new Date(value.getTime());
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? new Date(value) : undefined;
  }
  if (typeof value !== 'string') return undefined;
  return parseDateString(value);
}

export function coerceDateTime(value: DateLike): Date | undefined {
  if (!value && value !== 0) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : new Date(value.getTime());
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? new Date(value) : undefined;
  }
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Date.parse(trimmed);
  if (Number.isNaN(parsed)) return undefined;
  return new Date(parsed);
}

const ISO_MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;
const ISO_DATE_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function normalizeIsoMonth(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return ISO_MONTH_PATTERN.test(trimmed) ? trimmed : undefined;
}

export function normalizeIsoDate(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return ISO_DATE_PATTERN.test(trimmed) ? trimmed : undefined;
}

export function toIsoMonth(value: DateLike): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2]}`;
    }
  }

  const date = coerceDate(value);
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export function formatIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatIsoDateItShort(value: DateLike, fallback = '—'): string {
  const date = coerceDate(value);
  if (!date) return fallback;
  return IT_INPUT_DATE_FORMATTER.format(date);
}

export function formatIsoTimeItShort(value: DateLike, fallback = '—'): string {
  const date = coerceDateTime(value);
  if (!date) return fallback;
  return IT_INPUT_TIME_FORMATTER.format(date);
}

export function formatIsoDateTimeItShort(value: DateLike, fallback = '—'): string {
  const date = coerceDateTime(value);
  if (!date) return fallback;
  return IT_INPUT_DATE_TIME_FORMATTER.format(date);
}

export function formatIsoDateDDMMYYYY(isoDate: string): string {
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

export function formatIsoDateWithTokens(isoDate: string, format: string): string {
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return isoDate;

  const [, year, month, day] = match;
  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('M', String(parseInt(month, 10)))
    .replace('D', String(parseInt(day, 10)));
}

export function formatMonthYear(isoDate: string, locale: string): string {
  const match = isoDate.match(/^(\d{4})-(\d{2})/);
  if (!match) return isoDate;

  const [, year, month] = match;
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
  const formatted = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
  }).format(date);
  return capitalizeItalianMonth(formatted);
}

export function formatMonthYearInputValue(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  const monthYearMatch = trimmed.match(/^(\d{2})[/-](\d{4})$/);
  if (monthYearMatch) {
    return `${monthYearMatch[1]}/${monthYearMatch[2]}`;
  }

  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[2]}/${isoMatch[1]}`;
  }

  return trimmed;
}

export function formatItDateInputValue(value: unknown): string {
  const date =
    value instanceof Date ? value : typeof value === 'string' ? parseDateString(value) : undefined;
  if (!date) return '';
  return IT_INPUT_DATE_FORMATTER.format(date);
}

export function parseItDateInputToDate(value: string): Date | undefined {
  return parseDateInput(value);
}

export function formatDateToItDateInput(date: Date): string {
  return IT_INPUT_DATE_FORMATTER.format(date);
}

/**
 * Formats a date as a relative time string for recent dates (e.g., "1 ora fa", "Ieri", "3 giorni fa")
 * and falls back to absolute date format for older dates.
 *
 * @param value - The date to format
 * @param fallback - Fallback string if value is invalid
 * @param recentThresholdDays - Days within which to show relative time (default: 7)
 */
export function formatRelativeDate(
  value: DateLike,
  fallback = '—',
  recentThresholdDays = 7
): string {
  const date = coerceDateTime(value);
  if (!date) return fallback;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  // Future dates - use absolute
  if (diffMs < 0) {
    return IT_INPUT_DATE_FORMATTER.format(date);
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Within an hour
  if (diffMinutes < 1) {
    return 'Adesso';
  }
  if (diffMinutes < 60) {
    return diffMinutes === 1 ? '1 minuto fa' : `${diffMinutes} minuti fa`;
  }

  // Within a day
  if (diffHours < 24) {
    return diffHours === 1 ? '1 ora fa' : `${diffHours} ore fa`;
  }

  // Yesterday
  if (diffDays === 1) {
    return 'Ieri';
  }

  // Within threshold days
  if (diffDays <= recentThresholdDays) {
    return `${diffDays} giorni fa`;
  }

  // Older than threshold - use absolute date
  return IT_INPUT_DATE_FORMATTER.format(date);
}
